import { useState, useCallback, useMemo, type ReactNode, type MouseEvent } from 'react'
import { PreviewCard } from '@highlight/ui'
import { ChatPreview } from './chat-preview'
import { AudioNotePreview } from './audio-note-preview'
import { HighlightPreview } from './highlight-preview'
import type { ArtifactType } from '../artifact-url'
import { openArtifact } from '../open-artifact'
import { useHistoryByChatId } from '@renderer/hooks/chat-history'
import { useAudioNoteById } from '@renderer/hooks/audio-notes'
import { useInsightById } from '@renderer/windows/features/home/hooks/use-insights'
import { MessageSquareText, ListTodo, Mic, Link2 } from 'lucide-react'

interface ArtifactLinkProps {
  url: string
  artifactType: ArtifactType
  artifactId: string
  children: ReactNode
}

export function ArtifactLink({ url, artifactType, artifactId, children }: ArtifactLinkProps) {
  const [shouldFetch, setShouldFetch] = useState(false)

  // Conditionally fetch data on hover
  useHistoryByChatId(artifactType === 'conversation' && shouldFetch ? artifactId : undefined)
  useAudioNoteById(artifactType === 'audio-note' && shouldFetch ? artifactId : undefined)
  useInsightById(artifactType === 'insight' && shouldFetch ? artifactId : undefined)

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      openArtifact(artifactType, artifactId)
    },
    [artifactId, artifactType],
  )

  const handleMouseEnter = useCallback(() => {
    if (!shouldFetch) {
      setShouldFetch(true)
    }
  }, [shouldFetch])

  const renderPreview = useMemo(() => {
    switch (artifactType) {
      case 'conversation':
        return <ChatPreview chatId={artifactId} />
      case 'audio-note':
        return <AudioNotePreview audioNoteId={artifactId} />
      case 'insight':
        return <HighlightPreview highlightId={artifactId} />
      default:
        return (
          <div className="text-light/40 p-3">
            <p className="text-sm">Unknown artifact type</p>
          </div>
        )
    }
  }, [artifactType, artifactId])

  const renderIcon = useMemo(() => {
    switch (artifactType) {
      case 'conversation':
        return <MessageSquareText strokeWidth={1.5} />
      case 'audio-note':
        return <Mic size={20} className="text-green" />
      case 'insight':
        return <ListTodo strokeWidth={1.5} size={20} />
      default:
        return <Link2 strokeWidth={1.5} size={20} />
    }
  }, [artifactType])

  return (
    <PreviewCard>
      <PreviewCard.Trigger
        href={url}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        icon={renderIcon}
        className="text-light/60 group-hover/trigger:text-light/80 hover:!text-light text-wrap"
      >
        {children}
      </PreviewCard.Trigger>

      <PreviewCard.Content>{renderPreview}</PreviewCard.Content>
    </PreviewCard>
  )
}
